import {
  applyPLHorizon,
  calculatePLData,
  computePayrollByDept,
} from "@/lib/pl/plCalculations";
import { getPayrollDataById } from "@/hooks/payrollDataService";
import { useMemo, useState, useEffect } from "react";
import type { PayrollByDept } from "@/lib/pl/plCalculations";

export function usePLData(deal: any | undefined, yearCount: number = 10) {
  // ── 1. Load payroll from DB (critical — without this fallback weights are used) ──
  const [payrollByDept, setPayrollByDept] = useState<PayrollByDept | null>(
    null,
  );

  useEffect(() => {
    const dataId = deal?.kpis?.payroll?.dataId;
    if (!dataId) return;

    getPayrollDataById(dataId)
      .then((record) => {
        if (!record?.data?.positions) return;
        const pct = record.data.socialChargesPercent ?? 0;
        setPayrollByDept(computePayrollByDept(record.data.positions, pct));
      })
      .catch((e) => console.warn("[usePLData] payroll load failed:", e));
  }, [deal?.kpis?.payroll?.dataId]);

  // ── 2. Build P&L rows ─────────────────────────────────────────────────────
  const plData = useMemo(() => {
    if (!deal) return [];

    // Pass payrollByDept — calculatePLData uses it instead of weight-based fallback
    const baseRows = calculatePLData(deal, yearCount, payrollByDept);

    const exitSettings = deal.kpis?.exit;
    let exitYear: number | null = null;

    // Strategy-specific year wins — holdPeriodYears can be stale (e.g. =5 while sale.exitYear=7)
    if (exitSettings?.strategy === "SALE" && exitSettings.sale?.exitYear) {
      exitYear = exitSettings.sale.exitYear;
    } else if (
      exitSettings?.strategy === "REFINANCE" &&
      exitSettings.refinance?.refinanceYear
    ) {
      exitYear = exitSettings.refinance.refinanceYear;
    } else if (exitSettings?.holdPeriodYears != null) {
      exitYear = exitSettings.holdPeriodYears;
    }

    const horizon = exitYear ?? 10;

    console.log("usePLData debug:", {
      strategy: exitSettings?.strategy,
      holdPeriodYears: exitSettings?.holdPeriodYears,
      saleExitYear: exitSettings?.sale?.exitYear,
      resolvedExitYear: exitYear,
      resolvedHorizon: horizon,
      payrollLoaded: !!payrollByDept,
      financing: deal.kpis?.financing,
    });

    const roomsCount = deal.kpis?.rooms?.total || 0;
    const roomsCountByYear: number[] = Array.from(
      { length: yearCount },
      (_, idx) => {
        const phased = deal.roomsOpenByYear?.[idx];
        return (phased ?? roomsCount) || 0;
      },
    );

    const totalRevenueByYear =
      baseRows
        .find((r) => r.id === "total-revenue")
        ?.years.map((y) => y.total) ?? Array(10).fill(0);
    const roomsSoldByYear =
      baseRows.find((r) => r.id === "rooms-sold")?.years.map((y) => y.total) ??
      Array(10).fill(0);

    return applyPLHorizon(
      baseRows,
      horizon,
      totalRevenueByYear,
      roomsSoldByYear,
      roomsCountByYear,
    );
    // Re-run when payrollByDept loads (starts null, then fills in from DB)
  }, [deal, yearCount, payrollByDept]);

  // ── 3. Exit year (for UI banner & table zeroing) ──────────────────────────
  const exitYear = useMemo(() => {
    if (!deal) return null;
    const e = deal.kpis?.exit;
    if (!e) return null;
    if (e.strategy === "SALE" && e.sale?.exitYear) return e.sale.exitYear;
    if (e.strategy === "REFINANCE" && e.refinance?.refinanceYear)
      return e.refinance.refinanceYear;
    return e.holdPeriodYears ?? null;
  }, [deal]);

  const kpiRows = useMemo(
    () => plData.filter((r) => r.group === "KPIS"),
    [plData],
  );
  const operatingRows = useMemo(
    () => plData.filter((r) => r.group !== "KPIS"),
    [plData],
  );

  return { allRows: plData, kpiRows, operatingRows, exitYear };
}
